import { ChangeDetectorRef, Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from 'rxjs';

interface LoaderConfig {
  isLoading: boolean;
  type: 'full' | 'small' | 'inline';
  dismissible?: boolean;
  progress?: number; // 0-100 for determinate progress
  isIndeterminate?: boolean; // true for indeterminate progress
  message?: string; // Custom loading message
}

@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  private loadingType: 'full' | 'small' | 'inline' = 'full';
  private loadingSubject: BehaviorSubject<LoaderConfig> =
    new BehaviorSubject<LoaderConfig>({
      isLoading: false,
      type: this.loadingType,
      dismissible: false,
      progress: 0,
      isIndeterminate: true,
      message: 'Loading...'
    });

  public loading$: Observable<LoaderConfig> = this.loadingSubject.asObservable();

  show(type: 'full' | 'small' | 'inline' = 'full', dismissible: boolean = false, message?: string) {
    this.loadingType = type;
    this.loadingSubject.next({
      isLoading: true,
      type: this.loadingType,
      dismissible,
      progress: 0,
      isIndeterminate: true,
      message: message || 'Loading...'
    });
  }

  updateProgress(progress: number, message?: string) {
    const currentConfig = this.loadingSubject.value;
    if (currentConfig.isLoading) {
      this.loadingSubject.next({
        ...currentConfig,
        progress: Math.min(100, Math.max(0, progress)),
        isIndeterminate: false,
        message: message || currentConfig.message
      });
    }
  }

  setIndeterminate(message?: string) {
    const currentConfig = this.loadingSubject.value;
    if (currentConfig.isLoading) {
      this.loadingSubject.next({
        ...currentConfig,
        isIndeterminate: true,
        message: message || currentConfig.message
      });
    }
  }

  hide(delayInMs?: number) {
    // Delay the hide to allow for a smoother transition
    const delayInterval = delayInMs ?? (this.loadingType === 'inline' ? 200 : 200);
    setTimeout(() => {
      this.loadingSubject.next({
        isLoading: false,
        type: this.loadingType,
        dismissible: false,
        progress: 0,
        isIndeterminate: true,
        message: 'Loading...'
      });
    }, delayInterval);
  }
}
